// externals
import { RSAA } from 'redux-api-middleware';

// libraries
import { apiConfigHelper, locHrefUtil } from '@makemydeal/dr-shared-ui-utils';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// consts/enums
import {
    VAUTO_APPRAISALS_REQUEST,
    VAUTO_APPRAISALS_SUCCESS,
    VAUTO_APPRAISALS_FAILURE,
    VAUTO_APPRAISAL_LAUNCHED,
    VAUTO_APPRAISAL_SLIDEOUT_LOADED,
    VAUTO_APPRAISAL_MESSAGE_RECEIVED,
    VAUTO_APPRAISAL_MESSAGE_REJECTED
} from '../actionTypes/vAutoActionTypes';

// interfaces/types
import type {
    VAutoAppraisalLaunchedEventData,
    VAutoAppraisalMessageReceivedEventData,
    VAutoAppraisalMessageRejectedEventData,
    VAutoAppraisalSlideOutLoadedEventData
} from '../utils/vAutoAppraisalTelemetry';

// selectors
import { getCommonOrgId } from '../selectors/offerRedux';
import { getVin } from '../selectors/trade';
import * as tradeInsSelectors from '../selectors/tradeIns';

/**
 * @param isRefresh Marks a refetch the user should see progress for, as opposed to the fetch on deal
 * load. Carried as meta on all three actions so the reducer can pair a response with its request.
 */
export const getVAutoAppraisals =
    (appPrefix: string, platformUserId: string, isRefresh = false) =>
    (dispatch: any, getState: any) => {
        const state = getState();
        const commonOrgId = getCommonOrgId(state);
        // The trade-in form reads and writes VINs on the multi-trade-in slice for every deal, single
        // trade or not. `state.tradeIn` is only hydrated from the deal on load, so a trade-in added
        // (or VIN'd) in-session is invisible there and the fetch used to bail on an empty VIN. Fall
        // back to it only when the multi slice has nothing, for deal shapes that hydrate just `trade`.
        const tradeInVins = tradeInsSelectors.getAllTradeInVins(state).filter(Boolean);
        const legacyVin = getVin(state);
        const vins = (tradeInVins.length ? tradeInVins : [legacyVin].filter(Boolean)).join(',');
        const dealerId = dealerSelectors.getDealerId(state);
        const dealerName = dealerSelectors.getDealerName(state);
        const isTestDealer = dealerSelectors.getIsTestDealer(state);

        if (!vins || !commonOrgId || !platformUserId || platformUserId === 'undefined') {
            return;
        }

        const locHref = locHrefUtil.getLocHref();
        const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, '/vAutoAppraisals');
        const endpoint = `${bffApiUrlResult.url}?vin=${vins}`;

        // Copied onto all three descriptors so the response carries its own request's start time.
        const fetchStartTime = performance.now();

        dispatch({
            [RSAA]: {
                endpoint,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    PlatformUserId: platformUserId,
                    CommonOrgId: commonOrgId,
                    DealerId: dealerId,
                    DealerName: dealerName,
                    IsTestDealer: isTestDealer
                },
                types: [
                    { type: VAUTO_APPRAISALS_REQUEST, meta: { isRefresh, fetchStartTime } },
                    { type: VAUTO_APPRAISALS_SUCCESS, meta: { isRefresh, fetchStartTime } },
                    { type: VAUTO_APPRAISALS_FAILURE, meta: { isRefresh, fetchStartTime } }
                ]
            }
        });
    };

export const vAutoAppraisalLaunched = (payload: VAutoAppraisalLaunchedEventData) => ({
    type: VAUTO_APPRAISAL_LAUNCHED,
    payload
});

// `durationMs` / `dwellMs` are added by the telemetry middleware, which owns the timestamps.
export const vAutoAppraisalSlideOutLoaded = (payload: Omit<VAutoAppraisalSlideOutLoadedEventData, 'durationMs'>) => ({
    type: VAUTO_APPRAISAL_SLIDEOUT_LOADED,
    payload
});

export const vAutoAppraisalMessageReceived = (payload: Omit<VAutoAppraisalMessageReceivedEventData, 'dwellMs'>) => ({
    type: VAUTO_APPRAISAL_MESSAGE_RECEIVED,
    payload
});

export const vAutoAppraisalMessageRejected = (payload: VAutoAppraisalMessageRejectedEventData) => ({
    type: VAUTO_APPRAISAL_MESSAGE_REJECTED,
    payload
});
